import { useCallback, useEffect, useReducer, useState } from 'react'
import type { Reducer } from 'react'

import { Flex, LoadingSection } from 'app/components/design-system-components'
import Pagination from 'app/components/design-system-components/Pagination'
import ActivityCubeListItem from 'app/dashboard/team_activity/activity_cube_list_item/component'
import {
  ActivitiesFilters,
  filtersInitialState,
  getFilters,
} from 'app/dashboard/team_activity/ActivityFilters'
import { activityPeriods } from 'app/dashboard/team_activity/ActivityFilters/DateRangeFilter/constants'
import { getFinancialInsightsActivity } from 'app/dashboard/team_activity/FinancialInsightsReporting/api'
import { FinancialInsightsTable } from 'app/dashboard/team_activity/FinancialInsightsReporting/FinancialInsightsTable'
import {
  FI_PAGE_SIZE,
  THIRTY_DAYS_MS,
} from 'app/dashboard/team_activity/FinancialInsightsReporting/helper'
import type { FIActivityResponse } from 'app/dashboard/team_activity/FinancialInsightsReporting/helper'
import { error as notifyError } from 'app/services/snugNotifier'
import { formatFilterRangeToUTC } from 'app/utils/datetime/helpers'
import { useStateWithLoading } from 'app/utils/hooks/useStateWithLoading'
import {
  filterReducerFactory,
  filtersActions,
} from 'app/utils/reducers/filtersReducer'

const noop = (): void => {
  // ActivitiesFilters requires onPeriodChange but FI tab uses the
  // onFiltersInitialized callback exclusively to drive load().
}

// ── View (pure — testable with shallow) ──────────────────────────────────────

export interface LoadingState {
  state: string
  error: unknown
}

export interface FinancialInsightsListPageViewProps {
  loadingState: LoadingState
  data: FIActivityResponse | null
  page: number
  onPageChange: (page: number) => void
  onRetry: () => void
}

export function FinancialInsightsListPageView({
  loadingState,
  data,
  page,
  onPageChange,
  onRetry,
}: FinancialInsightsListPageViewProps) {
  const totalPages = data
    ? Math.max(1, Math.ceil(data.pagination.total / data.pagination.page_size))
    : 1

  return (
    <LoadingSection loadingState={loadingState} actionHandler={onRetry}>
      {data && (
        <>
          <Flex mt={6} flexWrap="wrap">
            <ActivityCubeListItem
              header="Bank connect started"
              value={String(data.cubes.sessions_created)}
            />
            <ActivityCubeListItem
              header="Reports completed"
              value={String(data.cubes.reports_completed)}
            />
            <ActivityCubeListItem
              header="Active consents (net)"
              value={String(data.cubes.active_consents_net)}
            />
            <ActivityCubeListItem
              header="PM views"
              value={String(data.cubes.pm_views)}
            />
          </Flex>
          <FinancialInsightsTable rows={data.activities} />
          {totalPages > 1 && (
            <Pagination
              canNextPage={page < totalPages}
              canPreviousPage={page > 1}
              currentPageNum={page}
              maxPageNumbers={totalPages}
              onPaginationButtonClicked={(selectedPage: number) =>
                onPageChange(selectedPage)
              }
            />
          )}
        </>
      )}
    </LoadingSection>
  )
}

// ── Container (wires hook + API) ─────────────────────────────────────────────

export interface FinancialInsightsListPageProps {
  teamID: string
}

type FiltersState = typeof filtersInitialState

export function FinancialInsightsListPage({
  teamID,
}: FinancialInsightsListPageProps) {
  const {
    state: data,
    setState: setData,
    loadingStates,
    loadingStatesHelpers,
  } = useStateWithLoading(null)

  const [filtersState, filtersDispatch] = useReducer(
    filterReducerFactory(filtersInitialState) as Reducer<FiltersState, unknown>,
    filtersInitialState,
  )

  const [filtersInitializationReady, setFiltersInitializationReady] =
    useState(false)

  const [page, setPage] = useState(1)

  const load = useCallback(
    (requestedPage: number) => {
      const { range = {} } = getFilters(filtersState)
      const formattedRange = formatFilterRangeToUTC(range)
      const from: string =
        formattedRange.startDate ??
        new Date(Date.now() - THIRTY_DAYS_MS).toISOString()
      const to: string = formattedRange.endDate ?? new Date().toISOString()

      loadingStatesHelpers.startLoading()
      getFinancialInsightsActivity(teamID, {
        from,
        to,
        page: requestedPage,
        pageSize: FI_PAGE_SIZE,
      })
        .then((resp: FIActivityResponse) => {
          setData(resp)
          loadingStatesHelpers.markDoneSuccessfully()
        })
        .catch((err: unknown) => {
          notifyError(err)
          loadingStatesHelpers.setError(err)
        })
    },
    [teamID, filtersState, loadingStatesHelpers, setData],
  )

  // Reset to page 1 whenever the date filter changes — paginating into
  // a stale filter window would surface rows that don't match the user's
  // selection.
  useEffect(() => {
    setPage(1)
  }, [filtersState])

  useEffect(() => {
    if (filtersInitializationReady) {
      load(page)
    }
    // load is intentionally omitted: useStateWithLoading recreates
    // loadingStatesHelpers each render, which would loop. teamID + filters
    // + page are the meaningful triggers and are listed explicitly.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teamID, filtersState, filtersInitializationReady, page])

  return (
    <>
      <ActivitiesFilters
        teamId={teamID}
        managers={[]}
        pods={[]}
        includeManagersFilter={false}
        onPeriodChange={noop}
        initialPeriod={activityPeriods.currentMonth}
        filters={filtersState.filters}
        updateFilter={(filterId: string, filterValues: unknown) =>
          filtersDispatch(filtersActions.updateFilter(filterId, filterValues))
        }
        onFiltersInitialized={() => setFiltersInitializationReady(true)}
        hasMultiTeamFilter={false}
        includePodsFilter={false}
      />
      <FinancialInsightsListPageView
        loadingState={loadingStates}
        data={data as FIActivityResponse | null}
        page={page}
        onPageChange={setPage}
        onRetry={() => load(page)}
      />
    </>
  )
}
